import type { JSX } from 'react';
import { hasFlag } from 'country-flag-icons';
import * as FlagIcons from 'country-flag-icons/react/3x2';
import { cn } from '@/utils/cn';
import { getPhoneCountry } from '@/utils/phoneCountries';

type CountryFlagProps = {
  code: string;
  /** Compact flag for the input prefix; slightly larger in the country list. */
  variant?: 'field' | 'list';
  className?: string;
};

type FlagComponent = (props: { className?: string; title?: string }) => JSX.Element;

const flagComponents = FlagIcons as Record<string, FlagComponent | undefined>;

export function CountryFlag({ code, variant = 'field', className }: CountryFlagProps) {
  const normalized = code.toUpperCase();
  const sizeClass = variant === 'field' ? 'h-3.5 w-5' : 'h-4 w-6';

  if (normalized === 'ZZ' || !hasFlag(normalized)) {
    const emoji = getPhoneCountry(normalized).flag;
    return (
      <span
        className={cn('inline-flex shrink-0 items-center justify-center leading-none', sizeClass, className)}
        aria-hidden
      >
        {emoji}
      </span>
    );
  }

  const FlagComponent = flagComponents[normalized];
  if (!FlagComponent) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-[2px] bg-slate-100 text-[10px] font-semibold uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300',
          sizeClass,
          className,
        )}
        aria-hidden
      >
        {normalized}
      </span>
    );
  }

  return (
    <FlagComponent
      title=""
      className={cn('shrink-0 rounded-[2px] object-cover', sizeClass, className)}
    />
  );
}
